import { Paper, Popper } from '@mui/material';
import { useEffect, useRef, useReducer } from 'react';
import { getAllHtz } from '../../part_search/service/HtzService';
import { useGridDataContext } from '../../../shared/context/GridDataContext';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import { Input } from '@airbus/components-react';
import { TableRes } from '../../../shared/types';
import styles from './HtzEditCell.module.css';
import { produce } from 'immer';

const MIN_CHARS = 4;

// Minimal FSM statuses
// Removing enum & namespace due to 'erasableSyntaxOnly' constraint, please propose if there is a cleaner approach outside of using const
const Status = {
  Initial: 'initial',
  Typing: 'typing',
  AssistLoading: 'assistLoading',
  AssistReady: 'assistReady',
} as const;

type Status = (typeof Status)[keyof typeof Status];

// Events for reducer
const Events = {
  SET_VALUE: 'SET_VALUE',
  SET_ASSIST: 'SET_ASSIST',
  SET_LOADING: 'SET_LOADING',
  OPEN_DROPDOWN: 'OPEN_DROPDOWN',
  RESET_ASSIST: 'RESET_ASSIST',
  COMMIT_EDIT: 'COMMIT_EDIT',
} as const;

type Events = (typeof Events)[keyof typeof Events];

type Action =
  | { type: typeof Events.SET_VALUE; value: string }
  | { type: typeof Events.SET_ASSIST; options: string[] }
  | { type: typeof Events.SET_LOADING; loading: boolean }
  | { type: typeof Events.OPEN_DROPDOWN }
  | { type: typeof Events.RESET_ASSIST }
  | { type: typeof Events.COMMIT_EDIT };

type State = {
  status: Status;
  value: string;
  assistOptions: string[];
  dropdownOpen: boolean;
};

function createInitialState(initValue: string): State {
  const trimmed = (initValue ?? '').trim();
  return {
    status: trimmed.length >= MIN_CHARS ? Status.Typing : Status.Initial,
    value: initValue ?? '',
    assistOptions: [],
    dropdownOpen: false,
  };
}

function reducer(state: State, event: Action): State {
  return produce(state, (draft) => {
    switch (event.type) {
      case Events.SET_VALUE: {
        draft.value = event.value;
        draft.status = event.value?.trim()?.length >= MIN_CHARS ? Status.Typing : Status.Initial;
        return;
      }
      case Events.SET_LOADING: {
        draft.status = event.loading ? Status.AssistLoading : draft.status;
        return;
      }
      case Events.SET_ASSIST: {
        draft.assistOptions = event.options || [];
        draft.status = draft.assistOptions.length > 0 ? Status.AssistReady : Status.Typing;
        return;
      }
      case Events.RESET_ASSIST: {
        draft.assistOptions = [];
        draft.status = Status.Initial;
        return;
      }
      case Events.OPEN_DROPDOWN: {
        draft.dropdownOpen = true;
        return;
      }
      case Events.COMMIT_EDIT: {
        draft.dropdownOpen = false;
        return;
      }
      default:
        return state;
    }
  });
}

const HtzEditCell = (prop: {
  value: string;
  colDef: any;
  id: number | string;
  field: string;
  row: any;
  tableRes: TableRes;
}) => {
  const { value: initialValue, colDef, id, field, row, tableRes } = prop;

  const [, setRows] = useGridDataContext();
  const apiRef = useGridApiContext();

  const anchorRef = useRef(null);
  const debounceRef = useRef<any>(null);

  const [state, dispatch] = useReducer(reducer, initialValue, createInitialState);

  const showAssist = colDef?.type === 'string' && (state.value?.length ?? 0) >= MIN_CHARS;
  const screenGridId: number | undefined = tableRes?.id;
  const schemaName: string | undefined = tableRes?.mbdDataGrid?.schemaName;

  const handleEnterTabBlur = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      const target = e.target as HTMLInputElement;
      target.blur();
    }
  };

  const commitEdit = (newVal: string) => {
    dispatch({ type: Events.COMMIT_EDIT });
    apiRef.current.stopCellEditMode({ id, field, ignoreModifications: false });
    setRows((prestate) => {
      const oldRow = prestate.find((item) => item.id === row.id);
      if (!oldRow) return prestate;
      if (oldRow[field] === newVal) {
        return prestate;
      }
      oldRow[field] = newVal;
      if (oldRow.status === 'N' || oldRow.status === 'C') {
        oldRow.touched[field] = true;
      }
      oldRow.status = oldRow.status || 'E';
      const newState = [...prestate];
      const index = newState.findIndex((item) => item.id === row.id);
      newState.splice(index, 1, oldRow);
      return newState;
    });
  };

  useEffect(() => {
    if (!showAssist) {
      dispatch({ type: Events.RESET_ASSIST });
      return;
    }
    const query = (state.value ?? '').trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      dispatch({ type: Events.SET_LOADING, loading: true });
      try {
        const data = await getAllHtz(query, screenGridId);
        const list = (data?.[`${schemaName}_qry_search`] ?? data ?? [])
          .map((item: any) => item?.htz)
          .filter(Boolean);
        dispatch({ type: Events.SET_ASSIST, options: list });
      } catch (e) {
        console.error(e);
        dispatch({ type: Events.SET_ASSIST, options: [] });
      } finally {
        dispatch({ type: Events.SET_LOADING, loading: false });
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [showAssist, state.value, screenGridId, schemaName]);

  const onStringOrNumberChangeHandler = (event) => {
    const newValue = event.target.value;
    dispatch({ type: Events.SET_VALUE, value: newValue });
    apiRef.current.setEditCellValue({ id, field, value: newValue });
    if (!prop.colDef?.onValueChange && newValue?.trim().length >= MIN_CHARS) {
      dispatch({ type: Events.OPEN_DROPDOWN });
    }
    if (colDef?.onValueChange) colDef.onValueChange({ rowId: id, value: newValue, row });
  };

  const handleSuggestionClick = (selectedHtz) => {
    dispatch({ type: Events.SET_VALUE, value: selectedHtz });
    dispatch({ type: Events.SET_ASSIST, options: [] });
    // Update grid internal value
    apiRef.current.setEditCellValue({ id, field, value: selectedHtz });
    // Commit immediately
    commitEdit(selectedHtz);
  };

  const isAssistLoading = state.status === Status.AssistLoading;
  const open =
    showAssist && state.dropdownOpen && (state.assistOptions.length > 0 || isAssistLoading);

  return (
    <div ref={anchorRef} className={styles.container}>
      <Input
        type='text'
        className='ds-input29 ds-input29--medium'
        value={state.value ?? ''}
        onChange={onStringOrNumberChangeHandler}
        onBlur={() => commitEdit(state.value)}
        onKeyDown={handleEnterTabBlur}
        id={id + '_' + field}
        autoComplete='off'
        autoFocus={true}
      />

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement='bottom-start'
        style={{ zIndex: 1301, width: anchorRef.current?.clientWidth }}
        modifiers={[
          {
            name: 'preventOverflow',
            options: { boundary: 'window' },
          },
        ]}
      >
        <Paper elevation={3} className={styles.popperPaper}>
          <ul className={styles.suggestionList}>
            {!isAssistLoading &&
              state.assistOptions.map((htzString, index) => (
                <li
                  key={index}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSuggestionClick(htzString)}
                  className={styles.suggestionItem}
                >
                  {htzString}
                </li>
              ))}
          </ul>
        </Paper>
      </Popper>
    </div>
  );
};

export default HtzEditCell;
